import { ThemeColor, themeColors } from '@/lib/config/colors';
import { useTheme } from '@/lib/utils/themeContext';
import classNames from 'classnames';
import { ChangeEventHandler } from 'react';

type ToggleProps = {
  label?: string;
  description?: string;
  checked: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: ThemeColor;
  hiddenIcon?: boolean;
  fadeOut?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

const Toggle = ({
  label,
  description,
  checked,
  size = 'md',
  color = 'purple',
  hiddenIcon,
  fadeOut = true,
  onChange,
}: ToggleProps) => {
  const theme = useTheme();

  return (
    <label className="mb-2 flex gap-4">
      <input type="checkbox" onChange={onChange} checked={checked} hidden />
      <div
        className={classNames(
          'focus:ring-cyan relative mt-0.5 inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
          {
            'h-5 w-9': size === 'sm',
            'h-6 w-11': size === 'md',
            'h-8 w-14': size === 'lg',
            [themeColors[color || theme.color][600].bg]: checked || !fadeOut,
            [themeColors[color || theme.color][200].bg]: !checked && fadeOut,
          }
        )}
      >
        {label && <span className="sr-only">{label}</span>}
        <span
          className={classNames(
            'pointer-events-none relative inline-block transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
            {
              'h-4 w-4': size === 'sm',
              'h-5 w-5': size === 'md',
              'h-6 w-6 translate-y-0.5': size === 'lg',
              'translate-x-4': size === 'sm' && checked,
              'translate-x-5': size === 'md' && checked,
              'translate-x-[1.6rem]': size === 'lg' && checked,
              'translate-x-0': size !== 'lg' && !checked,
              'translate-x-0.5': size === 'lg' && !checked,
            }
          )}
        >
          {!hiddenIcon && (
            <span
              className={classNames(
                checked ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
                'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
              )}
              aria-hidden="true"
            >
              <svg className="text-dark h-3 w-3" fill="none" viewBox="0 0 12 12">
                <path
                  d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
          {!hiddenIcon && (
            <span
              className={classNames(
                checked ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
                'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
              )}
              aria-hidden="true"
            >
              <svg className="text-cyan h-3 w-3" fill="currentColor" viewBox="0 0 12 12">
                <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
              </svg>
            </span>
          )}
        </span>
      </div>
      {label && (
        <div className="text-base">
          <div className="font-semibold">{label}</div>
          {description && <div>{description}</div>}
        </div>
      )}
    </label>
  );
};

export default Toggle;
